import Random from '../primitives/Random.js';
import { toHex } from '../primitives/utils.js';
const REDACTED = '[REDACTED]';
const MAX_ATTRIBUTES = 64;
const MAX_ATTRIBUTE_LENGTH = 512;
const MAX_ERROR_LENGTH = 2048;
const MAX_STACK_LENGTH = 8192;
let fallbackCorrelationSequence = 0;
const carrierContexts = new WeakMap();
const synchronousContextStack = [];
const SENSITIVE_TERMS = [
    'password',
    'passphrase',
    'privatekey',
    'presentationkey',
    'recoverykey',
    'snapshot',
    'mnemonic',
    'seed',
    'secret',
    'shamir',
    'share',
    'ciphertext',
    'plaintext',
    'authtoken',
    'accesstoken',
    'refreshtoken',
    'bearer',
    'otp',
    'onetime',
    'pin'
];
const SENSITIVE_LABEL_PATTERNS = SENSITIVE_TERMS.map(term => {
    const flexibleTerm = term.replace(/key|token|time/g, match => `[_ -]?${match}`);
    return new RegExp(String.raw `(${flexibleTerm}\s*["'=:]\s*)(?:"[^"]*"|'[^']*'|[^\s,;}]+)`, 'gi');
});
const BEARER_TOKEN = /\bBearer\s+[a-z0-9._~+/=-]+/gi;
const WIF_PRIVATE_KEY = /\b[KL][1-9A-HJ-NP-Za-km-z]{50,51}\b/g;
const EXTENDED_PRIVATE_KEY = /\b(?:xprv|tprv)[1-9A-HJ-NP-Za-km-z]+\b/g;
const HEX_256_BIT_VALUE = /\b[0-9a-fA-F]{64}\b/g;
const LARGE_ENCODED_BLOB = /\b[A-Za-z0-9+/=_-]{128,}\b/g;
const BYTE_VALUE = String.raw `(?:25[0-5]|2[0-4]\d|1?\d?\d)`;
const SERIALIZED_BYTE_ARRAY = new RegExp(String.raw `\[(?:\s*${BYTE_VALUE}\s*,){15,}\s*${BYTE_VALUE}\s*\]`, 'g');
const severityRank = {
    debug: 10,
    info: 20,
    warn: 30,
    error: 40
};
function truncate(value, maxLength) {
    if (value.length <= maxLength)
        return value;
    return `${value.slice(0, maxLength)}…`;
}
/**
 * Removes common secret encodings from diagnostic text. Unlabelled 256-bit
 * hex values are redacted because they may be private or presentation keys.
 */
export function sanitizeTelemetryText(value, maxLength = MAX_ERROR_LENGTH) {
    // Bound sanitizer work before applying regular expressions. Any suffix past
    // this window cannot reach the emitted value, even after redaction.
    const bounded = value.slice(0, Math.max(maxLength * 2, maxLength + 256));
    const labelsRedacted = SENSITIVE_LABEL_PATTERNS.reduce((sanitized, pattern) => sanitized.replace(pattern, '$1[REDACTED]'), bounded);
    return truncate(labelsRedacted
        .replace(BEARER_TOKEN, 'Bearer [REDACTED]')
        .replace(WIF_PRIVATE_KEY, REDACTED)
        .replace(EXTENDED_PRIVATE_KEY, REDACTED)
        .replace(HEX_256_BIT_VALUE, REDACTED)
        .replace(LARGE_ENCODED_BLOB, REDACTED)
        .replace(SERIALIZED_BYTE_ARRAY, REDACTED), maxLength);
}
function sanitizeName(value, fallback) {
    if (typeof value !== 'string' || value.trim().length === 0)
        return fallback;
    return truncate(value.trim().replace(/[^a-zA-Z0-9_.:-]/g, '_'), 160);
}
function sanitizeCorrelationId(value) {
    if (typeof value !== 'string' || value.length === 0)
        return undefined;
    return truncate(value.replace(/[^a-zA-Z0-9_.:-]/g, '_'), 128);
}
function sanitizeTraceId(value) {
    if (typeof value !== 'string')
        return undefined;
    const normalized = value.toLowerCase();
    if (!/^[0-9a-f]{32}$/.test(normalized) || /^0{32}$/.test(normalized))
        return undefined;
    return normalized;
}
function sanitizeSpanId(value) {
    if (typeof value !== 'string')
        return undefined;
    const normalized = value.toLowerCase();
    if (!/^[0-9a-f]{16}$/.test(normalized) || /^0{16}$/.test(normalized))
        return undefined;
    return normalized;
}
function sanitizeDuration(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined;
}
function sanitizeAttributeValue(name, value) {
    const normalizedName = name.toLowerCase().replaceAll(/[_ .-]/g, '');
    if (SENSITIVE_TERMS.some(term => normalizedName.includes(term)))
        return REDACTED;
    if (typeof value === 'string')
        return sanitizeTelemetryText(value, MAX_ATTRIBUTE_LENGTH);
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : undefined;
    if (typeof value === 'boolean')
        return value;
    if (value === null || value === undefined)
        return undefined;
    return REDACTED;
}
function sanitizeAttributes(attributes) {
    if (attributes == null)
        return undefined;
    const safe = {};
    let count = 0;
    for (const [rawName, value] of Object.entries(attributes)) {
        if (count >= MAX_ATTRIBUTES)
            break;
        const name = sanitizeName(rawName, '');
        if (name.length === 0)
            continue;
        const sanitized = sanitizeAttributeValue(name, value);
        if (sanitized === undefined)
            continue;
        safe[name] = sanitized;
        count++;
    }
    return count > 0 ? safe : undefined;
}
function sanitizeOptionalErrorText(value, maxLength) {
    return typeof value === 'string' ? sanitizeTelemetryText(value, maxLength) : undefined;
}
function sanitizeErrorCandidate(candidate, includeStack) {
    const message = typeof candidate.message === 'string' && candidate.message.length > 0
        ? candidate.message
        : 'Unknown error';
    const code = sanitizeOptionalErrorText(candidate.code, 120);
    const stack = includeStack
        ? sanitizeOptionalErrorText(candidate.stack, MAX_STACK_LENGTH)
        : undefined;
    return {
        name: sanitizeName(candidate.name, 'Error'),
        message: sanitizeTelemetryText(message),
        ...(code !== undefined ? { code } : {}),
        ...(stack !== undefined ? { stack } : {})
    };
}
function sanitizeError(error, includeStack) {
    if (error == null)
        return undefined;
    if (error instanceof Error || typeof error === 'object') {
        return sanitizeErrorCandidate(error, includeStack);
    }
    return {
        name: 'Error',
        message: sanitizeTelemetryText(typeof error === 'string' ? error : 'Unknown error')
    };
}
function sanitizeEvent(event, now, includeErrorStack) {
    const severity = event.severity ?? 'info';
    const correlationId = sanitizeCorrelationId(event.correlationId);
    const traceId = sanitizeTraceId(event.traceId);
    const spanId = sanitizeSpanId(event.spanId);
    const parentSpanId = sanitizeSpanId(event.parentSpanId);
    const startTimestamp = sanitizeDuration(event.startTimestamp);
    const durationMs = sanitizeDuration(event.durationMs);
    const attributes = sanitizeAttributes(event.attributes);
    const error = sanitizeError(event.error, includeErrorStack);
    return {
        name: sanitizeName(event.name, 'unknown'),
        component: sanitizeName(event.component, 'unknown'),
        severity,
        timestamp: typeof event.timestamp === 'number' &&
            Number.isFinite(event.timestamp)
            ? event.timestamp
            : now(),
        ...(event.type === 'span' || event.type === 'event' ? { type: event.type } : {}),
        ...(correlationId !== undefined ? { correlationId } : {}),
        ...(traceId !== undefined ? { traceId } : {}),
        ...(spanId !== undefined ? { spanId } : {}),
        ...(parentSpanId !== undefined ? { parentSpanId } : {}),
        ...(event.spanKind === 'internal' || event.spanKind === 'client' || event.spanKind === 'server'
            ? { spanKind: event.spanKind }
            : {}),
        ...(event.spanStatus === 'ok' ||
            event.spanStatus === 'error' ||
            event.spanStatus === 'cancelled'
            ? { spanStatus: event.spanStatus }
            : {}),
        ...(startTimestamp !== undefined ? { startTimestamp } : {}),
        ...(durationMs !== undefined ? { durationMs } : {}),
        ...(attributes !== undefined ? { attributes } : {}),
        ...(error !== undefined ? { error } : {})
    };
}
function defaultHighResolutionNow() {
    const perf = globalThis.performance;
    return perf != null && typeof perf.now === 'function' ? perf.now() : Date.now();
}
function randomTraceId() {
    return toHex(Random(16));
}
function randomSpanId() {
    return toHex(Random(8));
}
function mergeAttributes(first, second, third) {
    if (first == null && second == null && third == null)
        return undefined;
    return {
        ...first,
        ...second,
        ...third
    };
}
/**
 * A single timed operation. Ending a span is idempotent and cannot throw.
 */
export class TelemetrySpan {
    telemetry;
    name;
    component;
    kind;
    correlationId;
    initialAttributes;
    context;
    parentSpanId;
    startedAt;
    startedAtHighResolution;
    runtimeStart;
    ended = false;
    constructor(telemetry, name, component, kind, parent, correlationId, initialAttributes) {
        this.telemetry = telemetry;
        this.name = name;
        this.component = component;
        this.kind = kind;
        this.correlationId = correlationId;
        this.initialAttributes = initialAttributes;
        const now = telemetry.wallClock();
        this.startedAt = now();
        this.startedAtHighResolution = telemetry.monotonicClock()();
        this.context = {
            traceId: parent?.traceId ?? telemetry.createTraceId(),
            spanId: telemetry.createSpanId(),
            ...(parent?.traceFlags !== undefined ? { traceFlags: parent.traceFlags } : {})
        };
        this.parentSpanId = parent?.spanId;
        this.runtimeStart = telemetry.runtimeSnapshot();
    }
    bind(carrier) {
        this.telemetry.bindContext(carrier, this.context);
    }
    child(name, options) {
        return this.telemetry.startSpan(name, {
            ...options,
            parent: this.context,
            correlationId: options.correlationId ?? this.correlationId
        });
    }
    capture(name, attributes, severity = 'info') {
        this.telemetry.capture({
            name,
            component: this.component,
            severity,
            type: 'event',
            correlationId: this.correlationId,
            traceId: this.context.traceId,
            spanId: this.context.spanId,
            parentSpanId: this.parentSpanId,
            attributes
        });
    }
    end(options = {}) {
        if (this.ended)
            return;
        this.ended = true;
        const runtimeAttributes = this.telemetry.runtimeDiff(this.runtimeStart, this.telemetry.runtimeSnapshot());
        const durationMs = Math.max(0, this.telemetry.monotonicClock()() - this.startedAtHighResolution);
        const status = options.status ?? (options.error == null ? 'ok' : 'error');
        this.telemetry.capture({
            name: this.name,
            component: this.component,
            severity: options.severity ?? (status === 'error' ? 'error' : 'info'),
            type: 'span',
            correlationId: this.correlationId,
            traceId: this.context.traceId,
            spanId: this.context.spanId,
            parentSpanId: this.parentSpanId,
            spanKind: this.kind,
            spanStatus: status,
            startTimestamp: this.startedAt,
            durationMs,
            attributes: mergeAttributes(this.initialAttributes, runtimeAttributes, options.attributes),
            error: options.error
        });
    }
}
/**
 * Safe, no-op-by-default telemetry dispatcher.
 *
 * Consumer sink and beforeSend failures are intentionally isolated from wallet
 * and network behavior. `capture` never throws and never returns a rejecting
 * promise.
 */
export class Telemetry {
    config;
    constructor(config = {}) {
        this.config = config;
    }
    get enabled() {
        if (this.config.sink == null)
            return false;
        if (typeof this.config.enabled !== 'function')
            return this.config.enabled !== false;
        try {
            return this.config.enabled();
        }
        catch {
            return false;
        }
    }
    wallClock() {
        return this.config.now ?? Date.now;
    }
    monotonicClock() {
        return this.config.highResolutionNow ?? defaultHighResolutionNow;
    }
    createCorrelationId() {
        try {
            const custom = this.config.correlationIdFactory?.();
            if (typeof custom === 'string' && custom.length > 0) {
                return sanitizeCorrelationId(custom) ?? toHex(Random(16));
            }
            return toHex(Random(16));
        }
        catch {
            fallbackCorrelationSequence = (fallbackCorrelationSequence + 1) % Number.MAX_SAFE_INTEGER;
            return `${Date.now().toString(36)}-${fallbackCorrelationSequence.toString(36)}`;
        }
    }
    createTraceId() {
        try {
            const custom = sanitizeTraceId(this.config.traceIdFactory?.());
            return custom ?? randomTraceId();
        }
        catch {
            return randomTraceId();
        }
    }
    createSpanId() {
        try {
            const custom = sanitizeSpanId(this.config.spanIdFactory?.());
            return custom ?? randomSpanId();
        }
        catch {
            return randomSpanId();
        }
    }
    activeContext() {
        try {
            return this.config.contextManager?.active() ?? synchronousContextStack.at(-1);
        }
        catch {
            return synchronousContextStack.at(-1);
        }
    }
    contextFor(carrier) {
        if (carrier == null)
            return undefined;
        return carrierContexts.get(carrier);
    }
    bindContext(carrier, context) {
        carrierContexts.set(carrier, context);
    }
    linkContext(source, target) {
        if (source == null || target == null)
            return;
        const context = this.contextFor(source);
        if (context != null)
            this.bindContext(target, context);
    }
    startSpan(name, options) {
        const parent = options.parent ?? this.contextFor(options.carrier) ?? this.activeContext();
        const correlationId = options.correlationId ?? parent?.traceId ?? this.createCorrelationId();
        const span = new TelemetrySpan(this, name, options.component, options.kind ?? 'internal', parent, correlationId, options.attributes);
        if (options.carrier != null)
            span.bind(options.carrier);
        return span;
    }
    withSpan(name, options, callback) {
        const span = this.startSpan(name, options);
        const invoke = () => callback(span);
        let result;
        try {
            if (this.config.contextManager != null) {
                result = this.config.contextManager.run(span.context, invoke);
            }
            else {
                synchronousContextStack.push(span.context);
                try {
                    result = invoke();
                }
                finally {
                    synchronousContextStack.pop();
                }
            }
        }
        catch (error) {
            span.end({ status: 'error', error });
            throw error;
        }
        if (result != null && typeof result.then === 'function') {
            return Promise.resolve(result).then(value => {
                span.end();
                return value;
            }, error => {
                span.end({ status: 'error', error });
                throw error;
            });
        }
        span.end();
        return result;
    }
    runtimeSnapshot() {
        try {
            return this.config.runtimeMetrics?.snapshot();
        }
        catch {
            return undefined;
        }
    }
    runtimeDiff(start, end) {
        try {
            return this.config.runtimeMetrics?.diff(start, end);
        }
        catch {
            return undefined;
        }
    }
    capture(input) {
        if (!this.enabled)
            return;
        const minimum = this.config.minimumSeverity ?? 'info';
        const severity = input.severity ?? 'info';
        if (severityRank[severity] < severityRank[minimum])
            return;
        const now = this.config.now ?? Date.now;
        const includeStack = this.config.includeErrorStack === true;
        let event = sanitizeEvent(input, now, includeStack);
        try {
            const transformed = this.config.beforeSend?.(event);
            if (transformed === null)
                return;
            // Re-sanitize even when the hook returns undefined. TypeScript readonly
            // types do not prevent a JavaScript consumer from mutating the supplied
            // object in place.
            event = sanitizeEvent(transformed ?? event, now, includeStack);
        }
        catch {
            return;
        }
        try {
            const result = this.config.sink?.capture(event);
            if (result != null && typeof result.then === 'function') {
                void Promise.resolve(result).catch(() => {
                    /* telemetry must never break callers */
                });
            }
        }
        catch {
            // Telemetry must never break wallet or network behavior.
        }
    }
}
//# sourceMappingURL=Telemetry.js.map